import { useRef, useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Loader2, Upload } from "lucide-react";

/**
 * An image setting: paste a URL, or upload a file and let the server fill the
 * URL in. Both write the same field, so an admin who already has a hosted image
 * is not forced through an upload.
 */

type Props = {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** Uploads the file and resolves with the stored URL. */
  onUpload: (file: File) => Promise<string>;
  disabled?: boolean;
  accept?: string;
};

export function ImageUrlField({
  id,
  label,
  value,
  onChange,
  onUpload,
  disabled,
  accept = "image/png,image/jpeg,image/webp",
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    setIsUploading(true);
    try {
      const url = await onUpload(file);
      onChange(url);
    } catch (err) {
      const message = (
        err as { response?: { data?: { message?: string } } }
      )?.response?.data?.message;
      setError(message ?? "Upload failed.");
    } finally {
      setIsUploading(false);
      // Clear the input so picking the same file again still fires onChange.
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex gap-2">
        <Input
          id={id}
          type="url"
          value={value}
          disabled={disabled || isUploading}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://… or upload a file"
        />
        <Button
          type="button"
          variant="outline"
          disabled={disabled || isUploading}
          onClick={() => inputRef.current?.click()}
        >
          {isUploading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Upload className="h-4 w-4" />
          )}
          <span className="sr-only">Upload {label}</span>
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
      </div>
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
      {value ? (
        <img
          src={value}
          alt=""
          className="h-16 w-16 rounded-md object-cover border"
        />
      ) : null}
    </div>
  );
}

export default ImageUrlField;
